import type { RoleDetail } from '@skewb-works/contracts';
import { useMemo, useState } from 'react';
import {
  type RoleFormValues,
  grantKey,
  isAdminFromPermissions,
  permissionsFromSelection,
  selectionFromPermissions,
} from '../lib/rolePermissions';

export interface RoleForm {
  name: string;
  setName: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  selected: Set<string>;
  /**
   * Grant keys that are forced on and cannot be toggled off given the current selection
   * (a dependency implied by another grant). Today: any `Team` or `Planner` grant implies
   * `read Shift`, since Shifts touch both Teams and the Schedule — see {@link SHIFT_READ_KEY}.
   */
  lockedKeys: Set<string>;
  isAdmin: boolean;
  setIsAdmin: (value: boolean) => void;
  handleToggle: (action: string, subject: string, checked: boolean) => void;
  values: RoleFormValues;
  canSubmit: boolean;
}

/** `read Shift` — the implied dependency of holding any `Team` or `Planner` grant. */
const SHIFT_READ_KEY = grantKey('read', 'Shift');

/**
 * Whether the selection holds any grant that implies `read Shift`. Shifts are read off both
 * the Team surface and the Schedule (Planner) surface, so granting either must guarantee at
 * least read access to Shifts.
 */
function impliesShiftRead(selected: Set<string>): boolean {
  for (const key of selected) {
    if (key.endsWith('::Team') || key.endsWith('::Planner')) return true;
  }
  return false;
}

/**
 * Apply implied-grant rules to a selection: any `Team`/`Planner` grant forces `read Shift`
 * on. Returns a new set.
 */
function withImpliedGrants(selected: Set<string>): Set<string> {
  const next = new Set(selected);
  if (impliesShiftRead(next)) next.add(SHIFT_READ_KEY);
  return next;
}

/**
 * Owns the role form state and derived submit values. The role seeds the initial state once,
 * at mount — callers remount (via `key`) to load a different role, so a later change to the
 * `role` prop never overwrites in-progress edits.
 */
export function useRoleForm(role: RoleDetail | undefined): RoleForm {
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [selected, setSelected] = useState<Set<string>>(() =>
    withImpliedGrants(selectionFromPermissions(role?.permissions ?? [])),
  );
  const [isAdmin, setIsAdmin] = useState(() => isAdminFromPermissions(role?.permissions ?? []));

  const handleToggle = (action: string, subject: string, checked: boolean) => {
    const key = grantKey(action, subject);
    setSelected((prev) => {
      // `read Shift` is locked while a Team/Planner grant is held — ignore attempts to untick it.
      if (key === SHIFT_READ_KEY && !checked && impliesShiftRead(prev)) return prev;
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return withImpliedGrants(next);
    });
  };

  // `read Shift` is locked (forced on, non-removable) exactly while a Team/Planner grant is held.
  const lockedKeys = useMemo(
    () => (impliesShiftRead(selected) ? new Set([SHIFT_READ_KEY]) : new Set<string>()),
    [selected],
  );

  const values = useMemo<RoleFormValues>(
    () => ({
      name: name.trim(),
      description: description.trim() === '' ? undefined : description.trim(),
      permissions: permissionsFromSelection(isAdmin, selected),
    }),
    [name, description, isAdmin, selected],
  );

  const canSubmit = name.trim() !== '' && (isAdmin || selected.size > 0);

  return {
    name,
    setName,
    description,
    setDescription,
    selected,
    lockedKeys,
    isAdmin,
    setIsAdmin,
    handleToggle,
    values,
    canSubmit,
  };
}
